/**
 * Normalize provider event payload into stable market lists.
 * Provider shapes vary by match; keep bookmaker / match-odds / fancy separate.
 */

import { normalizeBetLimits } from "./clientUtils";

const norm = (v) => String(v || "").trim().toLowerCase();

const isBookmakerType = (t) => {
  const v = norm(t);
  return v === "bookmaker" || v === "bm" || v === "book maker";
};

const isFancyType = (t) => {
  const v = norm(t);
  if (!v) return false;
  return /fancy|session|khado|meter|odd.?even|line|fall|over/.test(v);
};

const itemKey = (item, prefix = "", index = 0) => {
  const id = item?.sid ?? item?.selectionId ?? item?.SelectionId ?? item?.nation ?? item?.name;
  return `${prefix}${id ?? `i${index}`}`;
};

const dedupePush = (list, seen, item, prefix) => {
  if (!item) return;
  const key = itemKey(item, prefix, list.length);
  if (seen.has(key)) return;
  seen.add(key);
  list.push({ ...item, _key: key });
};

/**
 * @param {object|null|undefined} eventData
 */
export function parseEventMarkets(eventData) {
  if (!eventData || typeof eventData !== "object") {
    return {
      bookmakers: [],
      fancyList: [],
      matchOdds: null,
      matchName: "Match",
      inPlay: false,
      bmMin: 100,
      bmMax: 200000,
      fancyMin: 100,
      fancyMax: 200000,
    };
  }

  const matchOdds = Array.isArray(eventData.Odds) ? eventData.Odds[0] || null : null;
  const runners = Array.isArray(matchOdds?.runners) ? matchOdds.runners : [];
  const teamNames = new Set(runners.map((r) => norm(r?.name)).filter(Boolean));

  const bookmakers = [];
  const fancyList = [];
  const seenBm = new Set();
  const seenFancy = new Set();

  const bookmakerBag = Array.isArray(eventData.Bookmaker) ? eventData.Bookmaker : [];
  bookmakerBag.forEach((row) => {
    const t = row?.t;
    if (isFancyType(t)) {
      dedupePush(fancyList, seenFancy, row, "f-");
      return;
    }
    if (isBookmakerType(t) || !norm(t)) {
      const name = norm(row?.nation);
      const hasPrice = Number(row?.b1) > 0 || Number(row?.l1) > 0;
      if (!hasPrice && name && teamNames.has(name)) return;
      dedupePush(bookmakers, seenBm, row, "bm-");
    }
  });

  const fancyBags = [
    eventData.Fancy2,
    eventData.Fancy,
    eventData.Fancy1,
    eventData.Session,
    eventData.Khado,
  ];
  fancyBags.forEach((bag) => {
    if (!Array.isArray(bag)) return;
    bag.forEach((row) => {
      const name = norm(row?.nation || row?.name);
      if (name && teamNames.has(name) && !isFancyType(row?.t)) {
        const looksLikeSession = /over|run|fall|wicket|session|only|boundary|six|four/i.test(
          String(row?.nation || "")
        );
        if (!looksLikeSession) return;
      }
      dedupePush(fancyList, seenFancy, row, "f-");
    });
  });

  fancyList.sort((a, b) => Number(a.srno || 0) - Number(b.srno || 0));

  const matchName =
    matchOdds?.matchName ||
    bookmakers[0]?.matchName ||
    fancyList[0]?.matchName ||
    "Match";

  const bmLimits = normalizeBetLimits(
    bookmakers[0]?.minBet ?? matchOdds?.minBet ?? 100,
    bookmakers[0]?.maxBet ?? matchOdds?.maxBet ?? 200000
  );
  const fancyLimits = normalizeBetLimits(
    fancyList[0]?.minBet ?? bmLimits.min,
    fancyList[0]?.maxBet ?? bmLimits.max
  );

  return {
    bookmakers,
    fancyList,
    matchOdds,
    matchName,
    inPlay: Boolean(matchOdds?.inplay),
    bmMin: bmLimits.min,
    bmMax: bmLimits.max,
    fancyMin: fancyLimits.min,
    fancyMax: fancyLimits.max,
  };
}

/** Lightweight fingerprint so we can skip useless state updates */
export function eventFingerprint(eventData) {
  if (!eventData) return "";
  try {
    const bm = (eventData.Bookmaker || [])
      .map((b) => `${b.sid}:${b.b1}:${b.l1}:${b.gstatus}`)
      .join("|");
    const fancy = (eventData.Fancy2 || eventData.Fancy || [])
      .map((f) => `${f.sid}:${f.b1}:${f.l1}:${f.gstatus}`)
      .join("|");
    const odds = (eventData.Odds?.[0]?.runners || [])
      .map((r) => {
        const back = r?.ex?.availableToBack?.[0]?.price ?? "";
        const lay = r?.ex?.availableToLay?.[0]?.price ?? "";
        return `${r.selectionId}:${back}:${lay}`;
      })
      .join("|");
    return `${bm}#${fancy}#${odds}`;
  } catch {
    return String(Date.now());
  }
}

export function isSuspendedPrice(value, gstatus) {
  if (String(gstatus || "").toUpperCase() === "SUSPENDED") return true;
  if (value == null || value === "" || value === "—" || value === "-") return true;
  const n = Number(value);
  return !Number.isFinite(n) || n <= 0;
}
